/**
 * Tool registry for the gateway AI agent.
 *
 * Same shape as the cseditor's registry, but slimmer:
 *   - One scope ("gateway") — no editor/flow scoping needed
 *   - No ajv dependency (the gateway frontend doesn't ship it). A tiny
 *     hand-rolled validator handles the common JSON-schema shapes our tool
 *     parameters use (object with required, type, enum, additionalProperties).
 *
 * Public API mirrors the editor's:
 *   const registry = buildToolRegistry(ctx, { tools });
 *   registry.schemas()        → OpenAI-format tool defs
 *   registry.has(name)        → boolean
 *   await registry.dispatch(name, args)
 *     → { ok: true, data }      on success
 *     → { ok: false, error }    on validation failure / tool failure
 */

import { scrubToolResultForModel } from "./publicVocabulary";

function failure(code, message, extra = {}) {
  return { ok: false, error: { code, message, ...extra } };
}

/**
 * Validate `args` against `schema`. Returns null on success, or an error
 * object describing the first problem. Schema shape supported (a subset of
 * JSON Schema enough for our tool params):
 *
 *   { type: "object",
 *     properties: { foo: { type: "string", enum: [...] }, ... },
 *     required: ["foo"],
 *     additionalProperties: false }
 *
 * Property `type` may be "string" | "number" | "integer" | "boolean" |
 * "array" | "object". `items` is honored on array properties for a single
 * level of nested validation.
 */
function validate(args, schema) {
  if (!schema) return null;
  if (schema.type === "object") {
    if (args == null || typeof args !== "object" || Array.isArray(args)) {
      return { code: "INVALID_ARGS", message: "Arguments must be an object." };
    }
    const required = schema.required || [];
    for (const key of required) {
      if (!(key in args)) {
        return {
          code: "INVALID_ARGS",
          message: `Missing required field "${key}".`,
        };
      }
    }
    if (schema.additionalProperties === false && schema.properties) {
      const allowed = new Set(Object.keys(schema.properties));
      for (const key of Object.keys(args)) {
        if (!allowed.has(key)) {
          return {
            code: "INVALID_ARGS",
            message: `Drop the "${key}" field — this tool's schema doesn't accept it.`,
          };
        }
      }
    }
    if (schema.properties) {
      for (const [key, propSchema] of Object.entries(schema.properties)) {
        if (!(key in args)) continue;
        const propErr = validateValue(args[key], propSchema, key);
        if (propErr) return propErr;
      }
    }
  }
  return null;
}

function validateValue(value, schema, path) {
  if (!schema) return null;
  const t = schema.type;
  if (t === "string") {
    if (typeof value !== "string")
      return { code: "INVALID_ARGS", message: `Field "${path}" must be a string.` };
    if (Array.isArray(schema.enum) && !schema.enum.includes(value)) {
      return {
        code: "INVALID_ARGS",
        message: `Field "${path}" must be one of: ${schema.enum.join(", ")}.`,
      };
    }
    if (typeof schema.minLength === "number" && value.length < schema.minLength) {
      return {
        code: "INVALID_ARGS",
        message: `Field "${path}" must be at least ${schema.minLength} character(s).`,
      };
    }
  } else if (t === "number") {
    if (typeof value !== "number")
      return { code: "INVALID_ARGS", message: `Field "${path}" must be a number.` };
  } else if (t === "integer") {
    if (typeof value !== "number" || !Number.isInteger(value))
      return { code: "INVALID_ARGS", message: `Field "${path}" must be an integer.` };
  } else if (t === "boolean") {
    if (typeof value !== "boolean")
      return { code: "INVALID_ARGS", message: `Field "${path}" must be a boolean.` };
  } else if (t === "array") {
    if (!Array.isArray(value))
      return { code: "INVALID_ARGS", message: `Field "${path}" must be an array.` };
    if (schema.items) {
      for (let i = 0; i < value.length; i++) {
        const itemErr = validateValue(value[i], schema.items, `${path}[${i}]`);
        if (itemErr) return itemErr;
      }
    }
  } else if (t === "object") {
    if (value == null || typeof value !== "object" || Array.isArray(value))
      return { code: "INVALID_ARGS", message: `Field "${path}" must be an object.` };
  }
  return null;
}

export function buildToolRegistry(ctx, { tools }) {
  if (!ctx || typeof ctx !== "object") {
    throw new Error("buildToolRegistry: ctx is required");
  }
  if (!Array.isArray(tools) || tools.length === 0) {
    throw new Error("buildToolRegistry: tools array is required");
  }
  const entries = new Map();
  for (const tool of tools) {
    if (!tool || typeof tool.name !== "string" || typeof tool.execute !== "function") {
      throw new Error("toolRegistry: tool missing required name/execute");
    }
    entries.set(tool.name, tool);
  }

  const schemas = () => {
    const out = [];
    for (const tool of entries.values()) {
      out.push({
        type: "function",
        function: {
          name: tool.name,
          description: tool.description,
          parameters: tool.parameters || { type: "object" },
        },
      });
    }
    return out;
  };

  const has = (name) => entries.has(name);

  /**
   * When a tool call fails validation, check whether the args the model
   * passed actually look like a DIFFERENT tool's signature. The classic
   * case (seen with Grok in the wild): model calls `navigate_to({text: …})`
   * intending to call `final_answer`. Without this hint, the error just
   * says "Missing required field sectionId" — the model can't tell it
   * picked the wrong tool entirely. With this, it gets:
   * "Missing required field sectionId. (Did you mean `final_answer`?
   * Your args match its required fields.)"
   */
  const suggestAlternativeTool = (calledName, args) => {
    if (!args || typeof args !== "object" || Array.isArray(args)) return null;
    const argKeys = Object.keys(args);
    if (argKeys.length === 0) return null;
    let bestName = null;
    let bestScore = 0;
    for (const [otherName, otherTool] of entries.entries()) {
      if (otherName === calledName) continue;
      const otherSchema = otherTool.parameters || {};
      const otherProps = otherSchema.properties || {};
      const otherRequired = otherSchema.required || [];
      if (otherRequired.length === 0) continue;
      const allRequiredPresent = otherRequired.every((k) => argKeys.includes(k));
      if (!allRequiredPresent) continue;
      // Args also can't include keys the other tool would reject.
      if (otherSchema.additionalProperties === false) {
        const allowed = new Set(Object.keys(otherProps));
        const allKeysAllowed = argKeys.every((k) => allowed.has(k));
        if (!allKeysAllowed) continue;
      }
      // Prefer the tool whose required-field count matches argKeys most
      // closely — exact match beats partial.
      const score =
        otherRequired.length - Math.abs(otherRequired.length - argKeys.length) * 0.5;
      if (score > bestScore) {
        bestScore = score;
        bestName = otherName;
      }
    }
    return bestName;
  };

  /**
   * Strip args the model passed that the tool's schema doesn't recognize,
   * BEFORE validation. Big real-world win: Grok kept stuffing `text` into
   * tools like `navigate_to`/`list_ai_providers`, triggering INVALID_ARGS
   * storms. With `additionalProperties: false`, those calls failed even
   * when required fields were also present. Stripping unknown keys lets
   * the call proceed and reduces round-trip explosions dramatically.
   *
   * Only strips when the schema explicitly forbids extras AND only the
   * unknown keys — required/known keys still go through validation as-is.
   */
  const stripUnknownArgs = (toolName, schema, args) => {
    if (!schema || schema.additionalProperties !== false) return args;
    if (!schema.properties || !args || typeof args !== "object" || Array.isArray(args)) {
      return args;
    }
    const allowed = new Set(Object.keys(schema.properties));
    const cleaned = {};
    let dropped = false;
    for (const [k, v] of Object.entries(args)) {
      if (allowed.has(k)) cleaned[k] = v;
      else dropped = true;
    }
    return dropped ? cleaned : args;
  };

  const dispatch = async (name, args) => {
    const tool = entries.get(name);
    if (!tool) return failure("UNKNOWN_TOOL", `No tool named "${name}"`);
    const rawInput = args == null ? {} : args;
    const input = stripUnknownArgs(name, tool.parameters, rawInput);
    const validationError = validate(input, tool.parameters);
    if (validationError) {
      const suggested = suggestAlternativeTool(name, rawInput);
      const hint = suggested
        ? ` (Did you mean to call \`${suggested}\`? Your args match its required fields.)`
        : "";
      return failure(
        validationError.code,
        validationError.message + hint,
        suggested ? { didYouMean: suggested } : undefined
      );
    }
    try {
      const result = await tool.execute(input, ctx);
      if (result == null || typeof result.ok !== "boolean") {
        return failure("APPLY_FAILED", `Tool "${name}" returned a malformed result`);
      }
      // Backstop only. Each tool is responsible for projecting its own payload
      // into operator language — this pass cannot know what a field means, so
      // it does the one thing that is always safe: replace content digests and
      // internal capability identifiers found inside free text. Resource
      // handles (runtime, connector, rollout, key ids) survive since BETA-28:
      // engineers debug with them, and hiding them from the people who own the
      // plant cost more than it protected. A tool that relies on this instead
      // of projecting is still a bug; the exposure guard test catches that.
      return scrubToolResultForModel(result);
    } catch (err) {
      return failure(
        "APPLY_FAILED",
        err && err.message ? String(err.message) : "Tool threw an exception"
      );
    }
  };

  return { schemas, has, dispatch };
}
